"use client";

import { useTranslations } from "next-intl";
import { type FormEvent, useState } from "react";
import { Button, Input, Label } from "@/components/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/auth";
import { useSecondFactorGate } from "@/context/second-factor-gate";
import {
  SecondFactorApiError,
  verifyPasswordSecondFactor,
} from "@/lib/auth-password-second-factor";

/**
 * Blocking gate for sessions that predate 2FA enforcement. When the backend
 * answers 403 `second_factor_required`, this modal traps the user on
 * public-only + logout until they enter their second-factor password, which
 * upgrades the session token in place (no wallet re-sign).
 */
export function SecondFactorGate() {
  const t = useTranslations("auth");
  const pending = useSecondFactorGate((s) => s.pending);
  const setPending = useSecondFactorGate((s) => s.setPending);
  const setToken = useAuth((s) => s.setToken);
  const setUser = useAuth((s) => s.setUser);
  const logout = useAuth((s) => s.logout);

  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!pending) return null;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!password || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      const res = await verifyPasswordSecondFactor(password);
      setToken(res.access_token);
      setUser(res.user);
      setPassword("");
      setPending(false);
    } catch (err) {
      const code = err instanceof SecondFactorApiError ? err.code : null;
      setError(
        code === "invalid_second_factor_password"
          ? t("login.passwordInvalid")
          : t("login.secondFactorError"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    setPending(false);
    setPassword("");
    await logout();
  };

  return (
    <Dialog open={pending}>
      <DialogContent
        className="max-w-xs space-y-4 px-9"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="pt-5 gap-2">
          <DialogTitle className="text-center">
            {t("secondFactorGate.title")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t("secondFactorGate.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="gate-second-factor-password">
              {t("login.secondFactorPasswordLabel")}
            </Label>
            <Input
              id="gate-second-factor-password"
              type="password"
              dir="ltr"
              autoComplete="current-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                if (error) setError(null);
              }}
              className={error ? "border-semantic-error" : undefined}
            />
            {error ? (
              <p className="text-xs leading-tight text-semantic-error ms-1.5">
                {error}
              </p>
            ) : null}
          </div>

          <div className="flex flex-row-reverse gap-3">
            <Button
              type="submit"
              variant="solid"
              colors="secondary"
              className="w-full"
              loading={submitting}
              disabled={!password || submitting}
            >
              {t("secondFactorGate.submit")}
            </Button>
            <Button
              type="button"
              variant="bordered"
              colors="secondary"
              className="w-full"
              onClick={handleLogout}
            >
              {t("secondFactorGate.logout")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
